import React, { useState } from "react";
import { ArrowLeft, Eye, EyeOff, KeyRound, LogIn, X } from "lucide-react";
import { recoverPassword } from "../api/auth";
import { applyDisplayPreferences, applyThemeClass } from "../utils/theme";

type Props = {
  onClose: () => void;
  onSwitchToSignup: () => void;
  initialMode?: "login" | "recovery";
};

const inputClass = `bs-auth-input w-full rounded-md border border-white/10 bg-white/[0.06] px-4 py-3 text-white
  placeholder-gray-500 transition-all duration-200 focus:border-cyan-300/60 focus:ring-2 focus:ring-cyan-300/15`;

const LoginModal: React.FC<Props> = ({ onClose, onSwitchToSignup, initialMode = "login" }) => {
  const [mode, setMode] = useState<"login" | "recovery">(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [showRecoveryPhrase, setShowRecoveryPhrase] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryPhrase, setRecoveryPhrase] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const switchMode = (next: "login" | "recovery") => {
    setMode(next);
    setError("");
    setSuccess("");
    setPassword("");
    setShowRecoveryPhrase(false);
    setRecoveryPhrase("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleLogin = async () => {
    if (!username.trim() || !password) {
      setError("Both fields are required.");
      return;
    }
    setError("");
    setSuccess("");
    setIsLoading(true);
    try {
      const base = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(/\/$/, "");
      const res = await fetch(`${base}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Login failed");

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      const theme = data.user?.theme || data.user?.settings?.theme || "AMOLED";
      const fontSize = data.user?.settings?.fontSize || "Medium";
      const glow = data.user?.settings?.glow !== false;
      localStorage.setItem("theme", theme);
      localStorage.setItem("fontSize", fontSize);
      localStorage.setItem("glow", glow ? "true" : "false");
      applyThemeClass(theme);
      applyDisplayPreferences(fontSize, glow);
      window.location.replace("/home");
    } catch (err: any) {
      setError(err?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecovery = async () => {
    const wordCount = recoveryPhrase.trim().split(/\s+/).filter(Boolean).length;
    if (!username.trim() || !recoveryPhrase || !newPassword || !confirmPassword) {
      setError("Fill every recovery field.");
      return;
    }
    if (wordCount !== 12) {
      setError("Enter the complete 12-word recovery phrase.");
      return;
    }
    if (newPassword.length < 8 || newPassword.length > 100) {
      setError("New password must be 8-100 characters.");
      return;
    }
    if (!/[A-Za-z]/.test(newPassword) || !/\d/.test(newPassword)) {
      setError("New password needs at least one letter and one number.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }

    setError("");
    setSuccess("");
    setIsLoading(true);
    try {
      const result = await recoverPassword({
        username: username.trim(),
        recoveryPhrase,
        newPassword,
      });
      setRecoveryPhrase("");
      setNewPassword("");
      setConfirmPassword("");
      setMode("login");
      setSuccess(result.message || "Password reset. Sign in with your new password.");
    } catch (err: any) {
      setError(err?.message || "Password recovery failed.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="contents">
      <div className="blackspace-auth-dialog w-full max-w-md outline-none" onClick={(event) => event.stopPropagation()}>
        <div className="blackspace-auth-card bs-auth-panel max-h-[calc(100dvh-1.5rem)] w-full overflow-y-auto p-5 text-white animate-fade-in sm:max-h-[calc(100dvh-2rem)] sm:p-7">
          <div className="bs-auth-heading mb-7 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-md border border-cyan-300/25 bg-cyan-300/10 p-2.5">
                {mode === "login" ? <LogIn size={23} className="text-cyan-200" /> : <KeyRound size={23} className="text-cyan-200" />}
              </div>
              <div>
                <h2 className="bg-gradient-to-r from-cyan-200 via-fuchsia-200 to-amber-100 bg-clip-text text-2xl font-black text-transparent">
                  {mode === "login" ? "Welcome back" : "Recover identity"}
                </h2>
                <p className="mt-1 text-xs font-medium text-gray-400">
                  {mode === "login" ? "Return to the room." : "Use the phrase you saved when this identity was created."}
                </p>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close sign in" className="rounded-md border border-white/10 p-2 text-gray-400 transition hover:bg-white/10 hover:text-white">
              <X size={20} />
            </button>
          </div>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (mode === "login") void handleLogin();
              else void handleRecovery();
            }}
            className="bs-auth-form space-y-4"
          >
            <div className="space-y-2">
              <label htmlFor="login-username" className="bs-auth-field-label block">Username</label>
              <input id="login-username" value={username} autoComplete="username" onChange={(event) => { setUsername(event.target.value); setError(""); }} placeholder="Enter your username" className={inputClass} />
            </div>

            {mode === "login" ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <label htmlFor="login-password" className="bs-auth-field-label">Password</label>
                  <button type="button" onClick={() => switchMode("recovery")} className="text-xs font-bold text-cyan-200 transition hover:text-white">Forgot password?</button>
                </div>
                <div className="relative">
                  <input id="login-password" type={showPassword ? "text" : "password"} value={password} autoComplete="current-password" onChange={(event) => { setPassword(event.target.value); setError(""); }} placeholder="Enter your password" className={`${inputClass} pr-12`} />
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setShowPassword((visible) => !visible);
                    }}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    title={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 transition hover:text-cyan-200"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <label htmlFor="login-recovery-phrase" className="bs-auth-field-label block">Recovery phrase</label>
                  <div className="relative">
                    <input id="login-recovery-phrase" type={showRecoveryPhrase ? "text" : "password"} value={recoveryPhrase} autoComplete="off" autoCapitalize="none" spellCheck={false} onChange={(event) => { setRecoveryPhrase(event.target.value); setError(""); }} placeholder="Enter all 12 words in order" className={`${inputClass} pr-12`} />
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setShowRecoveryPhrase((visible) => !visible);
                      }}
                      aria-label={showRecoveryPhrase ? "Hide recovery phrase" : "Show recovery phrase"}
                      aria-pressed={showRecoveryPhrase}
                      title={showRecoveryPhrase ? "Hide recovery phrase" : "Show recovery phrase"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 transition hover:text-cyan-200"
                    >
                      {showRecoveryPhrase ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <label htmlFor="login-new-password" className="bs-auth-field-label block">New password</label>
                  <input id="login-new-password" type="password" value={newPassword} autoComplete="new-password" onChange={(event) => { setNewPassword(event.target.value); setError(""); }} placeholder="8-100 characters with a letter and number" className={inputClass} />
                </div>
                <div className="space-y-2">
                  <label htmlFor="login-confirm-password" className="bs-auth-field-label block">Confirm new password</label>
                  <input id="login-confirm-password" type="password" value={confirmPassword} autoComplete="new-password" onChange={(event) => { setConfirmPassword(event.target.value); setError(""); }} placeholder="Repeat the new password" className={inputClass} />
                </div>
              </>
            )}

            {error && <div className="rounded-md border border-red-500/20 bg-red-500/10 p-3"><p className="text-center text-sm text-red-300" role="alert">{error}</p></div>}
            {success && <div className="rounded-md border border-emerald-400/20 bg-emerald-400/10 p-3"><p className="text-center text-sm text-emerald-200" role="status">{success}</p></div>}

            <div className="flex flex-row gap-2">
              {mode === "recovery" && (
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => switchMode("login")}
                  aria-label="Back to sign in"
                  title="Back to sign in"
                  className="bs-auth-back inline-flex items-center justify-center gap-2 transition disabled:opacity-50"
                >
                  <ArrowLeft size={18} /> Back
                </button>
              )}
              <button type="submit" disabled={isLoading} className="bs-auth-primary flex min-h-12 flex-1 items-center justify-center gap-2 rounded-md bg-gradient-to-r from-cyan-200 via-fuchsia-200 to-amber-100 px-6 py-3.5 font-bold text-black transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50">
                {isLoading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-black border-t-transparent" /> : mode === "login" ? <LogIn size={18} /> : <KeyRound size={18} />}
                {isLoading ? (mode === "login" ? "Signing in..." : "Resetting...") : mode === "login" ? "Sign in" : "Reset password"}
              </button>
            </div>

            <div className="text-center text-sm text-gray-400">
              Don&apos;t have an account?{" "}
              <button type="button" className="font-semibold text-cyan-200 transition hover:text-white" onClick={onSwitchToSignup}>Sign up</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
